interface BrianDatadogConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  /** Must match the backend's `DD_ENV` (dev / sandbox / prod) or RUM sessions
   * won't line up with APM traces in Datadog. */
  env: string;
  enabled: boolean;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  defaultPrivacyLevel: "mask" | "mask-user-input" | "allow";
}

interface BrianConfig {
  apiUrl: string;
  wsUrl: string;
  datadog: BrianDatadogConfig;
}

/** The `datadog` block is the only part of the config with defaults: CloudFront
 * can serve a cached `env-config.js` from before RUM existed, and inheriting
 * `enabled: false` beats reading `undefined` off a missing block. Local
 * development gets these too — the committed `env-config.js` sets no `datadog`
 * key, so localhost ships nothing to Datadog until someone opts in.
 *
 * Every default is the quiet choice: no credentials, disabled, no replay,
 * masked. A config file that enables RUM but forgets a field inherits silence
 * rather than capture. */
const datadogDefaults: BrianDatadogConfig = {
  applicationId: "",
  clientToken: "",
  site: "datadoghq.com",
  // Distinct from the backend's `brian-web` so RUM and APM stay separate
  // services in Datadog.
  service: "brian-frontend",
  env: "local",
  enabled: false,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  defaultPrivacyLevel: "mask",
};

/** What an `env-config.js` may actually set. It's hand-written plain JS outside
 * the type system, so every field is optional here — including individual keys
 * of `datadog`, which is why the merge below is a level deep. */
type ExternalBrianConfig = Partial<Omit<BrianConfig, "datadog">> & {
  datadog?: Partial<BrianDatadogConfig>;
};

declare global {
  interface Window {
    __SCRIBENOTE_BRIAN_CONFIG__?: ExternalBrianConfig;
  }
}

export function getConfig(): BrianConfig {
  const config = window.__SCRIBENOTE_BRIAN_CONFIG__;
  if (!config?.apiUrl || !config.wsUrl) {
    throw new Error(
      "Brian config not loaded. Ensure env-config.js is included before the app script.",
    );
  }
  return {
    apiUrl: config.apiUrl,
    wsUrl: config.wsUrl,
    // Merged a level deep so a config file carrying a partial `datadog` block
    // keeps the defaults for whatever it left out.
    datadog: { ...datadogDefaults, ...config.datadog },
  };
}
